import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Plus, Pencil, Trash2, Upload, FileText } from "lucide-react";
import { ShareButton } from "../../components/ShareButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ImageField = ({ value, onChange, testId }) => {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`${BACKEND_URL}${data.url}`);
      toast.success("Gambar berhasil diunggah");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="URL gambar atau unggah file →" data-testid={testId} />
        <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" ref={fileRef} onChange={upload} className="hidden" data-testid={`${testId}-file`} />
        <Button type="button" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} className="shrink-0" data-testid={`${testId}-upload-btn`}>
          <Upload size={15} className="mr-1.5" /> {uploading ? "Mengunggah..." : "Unggah"}
        </Button>
      </div>
      {value && <img src={value} alt="Pratinjau" className="h-24 rounded border border-slate-200 object-cover" data-testid={`${testId}-preview`} />}
    </div>
  );
};

export const FileField = ({ value, onChange, testId }) => {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-doc", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`${BACKEND_URL}${data.url}`);
      toast.success(`Dokumen "${data.filename}" berhasil diunggah`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="URL dokumen atau unggah file →" data-testid={testId} />
        <input type="file" accept=".pdf,.doc,.docx" ref={fileRef} onChange={upload} className="hidden" data-testid={`${testId}-file`} />
        <Button type="button" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} className="shrink-0" data-testid={`${testId}-upload-btn`}>
          <Upload size={15} className="mr-1.5" /> {uploading ? "Mengunggah..." : "Unggah PDF"}
        </Button>
      </div>
      {value && (
        <a href={value} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-bold text-sky-600 hover:underline" data-testid={`${testId}-preview`}>
          <FileText size={13} /> Lihat dokumen terunggah
        </a>
      )}
    </div>
  );
};

export const ContentManager = ({ config }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(config.listUrl).then((r) => setItems(r.data)).catch(() => {});
  }, [config.listUrl]);

  useEffect(() => { load(); }, [load]);

  const emptyForm = () => Object.fromEntries(config.fields.map((f) => [f.key, f.type === "number" ? 0 : f.options?.[0] ?? ""]));

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (item) => {
    setEditing(item);
    setForm(Object.fromEntries(config.fields.map((f) => [f.key, item[f.key] ?? ""])));
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      config.fields.forEach((f) => { if (f.type === "number") payload[f.key] = Number(payload[f.key]) || 0; });
      if (editing) {
        await api.put(`/admin/${config.name}/${editing.id}`, payload);
        toast.success("Data diperbarui");
      } else {
        await api.post(`/admin/${config.name}`, payload);
        toast.success("Data ditambahkan");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus data ini?")) return;
    await api.delete(`/admin/${config.name}/${id}`);
    toast.success("Data dihapus");
    load();
  };

  return (
    <div data-testid={`manager-${config.name}`}>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading text-xl font-bold text-slate-900">{config.title}</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="bg-orange-600 hover:bg-orange-500 rounded-full px-5 font-bold" data-testid={`${config.name}-add-btn`}>
              <Plus size={16} className="mr-1" /> Tambah
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">{editing ? "Edit" : "Tambah"} {config.title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {config.fields.map((f) => (
                <div key={f.key} className="space-y-1.5">
                  <Label>{f.label}</Label>
                  {f.type === "textarea" ? (
                    <Textarea rows={4} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`${config.name}-field-${f.key}`} />
                  ) : f.type === "image" ? (
                    <ImageField value={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v })} testId={`${config.name}-field-${f.key}`} />
                  ) : f.type === "file" ? (
                    <FileField value={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v })} testId={`${config.name}-field-${f.key}`} />
                  ) : f.type === "select" ? (
                    <Select value={String(form[f.key])} onValueChange={(v) => setForm({ ...form, [f.key]: f.options.includes(true) ? v === "true" : v })}>
                      <SelectTrigger data-testid={`${config.name}-field-${f.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {f.options.map((o, i) => (
                          <SelectItem key={String(o)} value={String(o)}>{f.optionLabels ? f.optionLabels[i] : String(o)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input type={f.type} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`${config.name}-field-${f.key}`} />
                  )}
                </div>
              ))}
              <Button onClick={save} disabled={saving} className="w-full bg-orange-600 hover:bg-orange-700" data-testid={`${config.name}-save-btn`}>
                {saving ? "Menyimpan..." : "Simpan"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="border border-slate-200 rounded-xl overflow-x-auto bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-white sticky top-0">
            <tr>
              {config.columns.map((c) => <th key={c.key} className="text-left px-4 py-3 font-heading">{c.label}</th>)}
              <th className="text-right px-4 py-3 font-heading">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {items.length === 0 && (
              <tr><td colSpan={config.columns.length + 1} className="px-4 py-10 text-center text-slate-400">Belum ada data.</td></tr>
            )}
            {items.map((item) => (
              <tr key={item.id} className="hover:bg-orange-50/50" data-testid={`${config.name}-row-${item.id}`}>
                {config.columns.map((c) => (
                  <td key={c.key} className="px-4 py-3 text-slate-700 max-w-xs truncate">{c.render ? c.render(item[c.key]) : String(item[c.key] ?? "")}</td>
                ))}
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {config.sharePath && (
                    <ShareButton path={config.sharePath(item)} title={config.shareTitle ? config.shareTitle(item) : item.title} testId={`${config.name}-share-${item.id}`} />
                  )}
                  <Button variant="ghost" size="icon" onClick={() => openEdit(item)} data-testid={`${config.name}-edit-${item.id}`}>
                    <Pencil size={15} className="text-slate-500" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(item.id)} data-testid={`${config.name}-delete-${item.id}`}>
                    <Trash2 size={15} className="text-red-500" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
